import { enableProdMode, inject, provideZonelessChangeDetection } from "@angular/core";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { importProvidersFrom } from "@angular/core";
import { provideFirebaseApp } from "@angular/fire/app";
import { initializeApp } from "@angular/fire/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { getFirestore, provideFirestore } from "@angular/fire/firestore";
import {
  getFunctions,
  provideFunctions,
} from "@angular/fire/functions";
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { provideRouter, withNavigationErrorHandler } from "@angular/router";
// import { environment } from './environments/environment';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "./environment";
import { AIService } from "./app/common/services/ai-service";
import { AppComponent } from "./app/app.component";
import { initializeApp as firebaseInitializeApp } from "firebase/app";
import { getFirestore as firebaseGetFirestore } from "firebase/firestore";
import { datadogRum } from "@datadog/browser-rum";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
} from "@angular/material/tooltip";
import { authGuard } from "./app/common/guards/auth.guard";
import { portfolioManagementRoutes, portfolioManagementRedirects } from "./app/parse-analyze-track/components/portfolio-workspace/portfolio-management.routes";
import { portfolioAccessGuard } from "./app/parse-analyze-track/guards/portfolio-access.guard";
import { ChunkLoadRecoveryService } from "./app/common/services/chunk-load-recovery.service";

// When ready for production, implement JWT authentication with Firebase

console.info("[DeployMarker] full-build-deploy-test-2026-05-21");

datadogRum.init({
  applicationId: "3cb977b6-70e8-4d02-8b82-a05bc54cb476",
  clientToken: "pub76bff883dc9c1187fa71aca67df739ba",
  site: "us5.datadoghq.com",
  service: "tradeparse",
  version: environment.datadog.version,
  env: environment.datadog.env,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  defaultPrivacyLevel: "allow",
});

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),provideAnimations(),
    {
      // Tooltips wait for hover intent instead of firing on pass-over; a tooltip
      // that must open at once (help "(?)" triggers) uses TooltipToggleDirective.
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 400,
        hideDelay: 0,
        touchendHideDelay: 1500,
      } satisfies MatTooltipDefaultOptions,
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    provideFunctions(() => getFunctions(undefined, "us-west2")),
    provideHttpClient(withInterceptorsFromDi()),
    AIService,
    provideRouter([
      {
        path: "",
        loadComponent: () =>
          import("./app/home/coming-soon/coming-soon.component").then(
            (m) => m.ComingSoonComponent
          ),
      },
      {
        // Temporary unlinked URL for the full landing page until launch.
        path: "preview",
        loadComponent: () =>
          import("./app/home/landing/landing.component").then(
            (m) => m.LandingComponent
          ),
      },
      {
        // Unlinked private-beta application page (noindex); submissions go
        // to the backend's anonymous POST /api/v1/beta-applications.
        path: "private-beta",
        loadComponent: () =>
          import("./app/home/private-beta/private-beta.component").then(
            (m) => m.PrivateBetaComponent
          ),
      },
      {
        path: "team",
        loadComponent: () =>
          import("./app/home/home-team.component").then(
            (m) => m.HomeTeamComponent
          ),
      },
      {
        path: "login",
        loadComponent: () =>
          import("./app/common/components/login/login.component").then(
            (m) => m.LoginComponent
          ),
      },
      {
        path: "authorize",
        loadComponent: () =>
          import("./app/common/auth-schwab/authorize.component").then(
            (m) => m.AuthorizeComponent
          ),
      },
      {
        path: "signup",
        loadComponent: () =>
          import("./app/common/components/signup/signup.component").then(
            (m) => m.SignupComponent
          ),
      },
      {
        path: "forgot-password",
        loadComponent: () =>
          import("./app/common/components/forgot-password/forgot-password.component").then(
            (m) => m.ForgotPasswordComponent
          ),
      },
      {
        path: "reset-password",
        loadComponent: () =>
          import("./app/common/components/reset-password/reset-password.component").then(
            (m) => m.ResetPasswordComponent
          ),
      },
      {
        path: "swb",
        loadComponent: () =>
          import("./app/common/auth-schwab/response-token.component").then(
            (m) => m.AuthRespSchwabComponent
          ),
      },
      { path: "experiments", redirectTo: "parse-analyze-track/experiments", pathMatch: "full" },
      {
        // The assistant's popout: a blank chat-only surface, outside the app
        // shell so nothing renders behind the full-screen sheet.
        path: "chat",
        canActivate: [authGuard],
        loadComponent: () =>
          import("./app/agent-chat/agent-chat-popout.component").then(
            (m) => m.AgentChatPopoutComponent
          ),
      },
      {
        path: "parse-analyze-track",
        loadComponent: () =>
          import("./app/shell/shell.component").then((m) => m.ShellComponent),
        children: [
          ...portfolioManagementRoutes,
          ...portfolioManagementRedirects,
          {
            path: "",
            loadComponent: () =>
              import(
                "./app/parse-analyze-track/components/pat-date-range/pat-date-range.component"
              ).then((m) => m.PatDateRangeComponent),
            children: [
              {
                path: "",
                redirectTo: "dashboard",
                pathMatch: "full",
              },
              {
                path: "dashboard",
                canActivate: [authGuard],
                loadComponent: () =>
                  import(
                    "./app/parse-analyze-track/components/dashboard/dashboard.component"
                  ).then((m) => m.DashboardComponent),
              },
              {
                path: "portfolio",
                redirectTo: "portfolio/portfolio",
                pathMatch: "full",
              },
              {
                path: "portfolio/:tab",
                canActivate: [portfolioAccessGuard],
                loadComponent: () =>
                  import(
                    "./app/parse-analyze-track/components/portfolio-ai/portfolio-ai.component"
                  ).then((m) => m.PortfolioAiComponent),
              },
              {
                path: "experiments",
                loadComponent: () => import("./app/parse-analyze-track/components/public-experiments/public-experiments.component").then((m) => m.PublicExperimentsComponent),
              },
              {
                path: "portfolios",
                loadComponent: () =>
                  import(
                    "./app/parse-analyze-track/components/public-portfolios/public-portfolios.component"
                  ).then((m) => m.PublicPortfoliosComponent),
              },
              {
                path: "history",
                canActivate: [authGuard],
                loadComponent: () =>
                  import(
                    "./app/parse-analyze-track/components/trade-history/trade-history.component"
                  ).then((m) => m.TradeHistoryComponent),
              },
              {
                path: "ai-assistant",
                canActivate: [authGuard],
                loadComponent: () =>
                  import(
                    "./app/parse-analyze-track/components/ai-assistant/ai-assistant.component"
                  ).then((m) => m.AIAssistantComponent),
              },
            ],
          },
          {
            path: "settings", // '/parse-analyze-track/settings' → default tab
            redirectTo: "settings/general",
            pathMatch: "full",
          },
          {
            path: "settings/:tab", // settings/general | settings/chat-accounts
            canActivate: [authGuard],
            loadComponent: () =>
              import(
                "./app/parse-analyze-track/components/settings-page/settings-page.component"
              ).then((m) => m.SettingsPageComponent),
          },
          {
            path: "walkthroughs", // the only place tours and walkthroughs start
            canActivate: [authGuard],
            loadComponent: () =>
              import(
                "./app/walkthroughs/walkthroughs-page/walkthroughs-page.component"
              ).then((m) => m.WalkthroughsPageComponent),
          },
        ],
      },
    ], withNavigationErrorHandler((event) => {
      inject(ChunkLoadRecoveryService).handleNavigationError(event);
    })),
  ],
}).catch((err) => console.error(err));
